import {Subscription} from "rxjs";
import {RouterOutlet} from "@angular/router";
import {isPlatformBrowser} from "@angular/common";
import {Component, HostListener, inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';

import {FooterComponent, NavbarComponent} from "@numizmat/components";
import {AdHubService, MetaTagService, UserHubService} from "@numizmat/services/common";
import {ImageViewerComponent, ScrollTopButtonComponent} from "@numizmat/components";
import {AccessTokenService, CurrentUserApiService, CurrentUserStateService} from "@numizmat/services/account";

@Component({
  selector: 'nz-root',
  standalone: true,
  imports: [
    RouterOutlet,
    NavbarComponent,
    FooterComponent,
    ImageViewerComponent,
    ScrollTopButtonComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  private platformId: Object = inject(PLATFORM_ID);
  private adHubService: AdHubService = inject(AdHubService);
  private userHubService: UserHubService = inject(UserHubService);
  private metaTagService: MetaTagService = inject(MetaTagService);
  private accessTokenService: AccessTokenService = inject(AccessTokenService);
  private currentUserApiService: CurrentUserApiService = inject(CurrentUserApiService);
  private currentUserStateService: CurrentUserStateService = inject(CurrentUserStateService);

  private subs: Subscription = new Subscription();

  @HostListener('window:beforeunload', ['$event'])
  async handleBeforeUnload(event: Event): Promise<void> {
    event.stopImmediatePropagation();
    try {
      await this.adHubService.removeFromGroup();
      await this.adHubService.stopConnection();
      this.adHubService.offReceiveNotification();
      await this.userHubService.removeFromGroup();
      await this.userHubService.stopConnection();
      this.userHubService.offReceiveNotification();
    } catch (_) {
    }
  }

  public ngOnInit(): void {
    this.getCurrentUser();
    this.createCurrentUserSubscription();
    this.metaTagService.setMainTags();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private getCurrentUser(): void {
    this.currentUserApiService.getCurrentUser().subscribe({
      next: response => {
        if (response.success) {
          this.accessTokenService.setAccessToken(response.refreshToken);
          this.currentUserStateService.setCurrentUser({
            loggedIn: true,
            userData: {
              userId: response.userId,
              username: response.username,
              newMessageCount: response.newMessageCount,
              newNotificationCount: response.newNotificationCount,
              confirmationMethod: response.confirmationMethod,
              confirmationMethodName: response.confirmationMethodName
            }
          });
        } else {
          this.accessTokenService.deleteAccessToken();
          this.currentUserStateService.setCurrentUser({
            loggedIn: false
          })
        }
      }
    });
  }

  private createCurrentUserSubscription() {
    const subscription: Subscription = this.currentUserStateService.getCurrentUser().subscribe({
      next: currentUser => {
        if (!isPlatformBrowser(this.platformId)) {
          return;
        }
        if (currentUser.loggedIn) {
          this.userHubService.startConnection().then(() => {
            this.userHubService.addToGroup(currentUser!.userData!.userId).then();
          });
        } else {
          this.userHubService.removeFromGroup().then(() => {
            this.userHubService.stopConnection().then();
          });
        }
      }
    });
    this.subs.add(subscription);

    this.userHubService.onReceiveNotification((data: any) => {
      this.currentUserStateService.updateCurrentUserData({
        newMessageCount: data.newMessageCount,
        newNotificationCount: data.newNotificationCount
      });
    });
  }
}
